import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const TERMS = [
  {
    title: "Nature of services",
    text: "Sharpin Healthcare Services acts as a professional facilitator and provider of homecare staffing — caretakers, nurses, baby caretakers and japa maids — deployed at the client's residence. Our staff provide supportive care and do not replace the treating physician's medical judgment.",
  },
  {
    title: "Limitation of liability",
    text: "Sharpin Healthcare Services shall not be held liable for any direct, indirect, incidental, or consequential loss, injury, or damage incurred by any person, patient, or family member during or arising out of homecare services, staff deployment, or equipment usage.",
  },
  {
    title: "Medical direction",
    text: "Clients are advised to verify all patient medical orders, medication schedules and clinical procedures with the patient's primary physician. Any clinical task performed by nursing staff must be backed by a valid doctor's prescription.",
  },
  {
    title: "Bookings & replacement",
    text: "Bookings are confirmed only after assessment and advance payment as communicated by our care team. If deployed staff is unavailable or unsuitable, a replacement is arranged at no additional charge.",
  },
  {
    title: "Client responsibilities",
    text: "Clients must provide a safe working environment, accurate patient history, and must not engage deployed staff in tasks outside the agreed scope of care.",
  },
];

const Terms = () => (
  <section id="terms" className="py-16 md:py-24 bg-white border-t border-slate-200" data-testid="terms-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
      <SectionHeading
        number="06 — Terms of Service"
        title="Clear terms for"
        accent="home care."
        id="terms"
      />

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8">
          {TERMS.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="flex gap-6"
              data-testid={`terms-item-${i}`}
            >
              <span className="font-display text-lg font-bold text-tealbrand shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold text-navy mb-2">{t.title}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{t.text}</p>
              </div>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="pt-4"
          >
            <Link
              to="/terms"
              data-testid="terms-full-link"
              className="inline-flex items-center gap-2 bg-navy text-white font-semibold px-7 py-3.5 rounded-full hover:bg-tealbrand transition-colors duration-300"
            >
              Read Full Terms of Service
            </Link>
          </motion.div>
        </div>

        <motion.aside
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="bg-navy rounded-3xl p-8 md:p-10 sticky top-28" data-testid="liability-disclaimer-card">
            <div className="h-12 w-12 rounded-2xl bg-tealbrand/20 flex items-center justify-center mb-6">
              <ShieldAlert className="h-6 w-6 text-tealbrand-light" />
            </div>
            <h3 className="font-display text-xl font-bold text-white mb-4">
              Liability Disclaimer
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              Sharpin Healthcare Services acts as a professional facilitator and service
              provider. Sharpin Healthcare Services shall not be held liable for any direct,
              indirect, incidental, or consequential loss, injury, or damage incurred by any
              person, patient, or family member during or arising out of homecare services,
              staff deployment, or equipment usage. Clients are advised to verify patient
              medical orders with primary physicians.
            </p>
          </div>
        </motion.aside>
      </div>
    </div>
  </section>
);

export default Terms;
